import { Divider } from '@mui/material'
import CustomInput from '../../components/TextInput'
import CustomPaper from '../../containers/Paper'
import CustomButton from '../../components/Button'
import { useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useState } from 'react'
import { useSnackbar } from 'notistack'
import useSignIn from 'react-auth-kit/hooks/useSignIn'
import useIsAuthenticated from 'react-auth-kit/hooks/useIsAuthenticated'
import { AppRoutes } from '../../helpers/routes'
import { isAdmin, isRootAdmin, validateEmail } from '../../helpers/utils'
import AuthService from '../../services/AuthService'
import { AuthenticatedUserData, LoginResponse } from '../../models/auth'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import LinkedInIcon from '@mui/icons-material/LinkedIn'

const DEFAULT_ERRORS = {
    email: '',
    password: '',
}

const linkedinOAuthURL = `${process.env.REACT_APP_LINKEDIN_OAUTH_URL}?response_type=code&client_id=${process.env.REACT_APP_LINKEDIN_CLIENT_ID}&redirect_uri=${encodeURIComponent(`${process.env.REACT_APP_BASE_URL as string}${AppRoutes.activateLikedinUser}`)}&scope=openid profile email`

const LoginPage = () => {
    const signIn = useSignIn()
    const navigate = useNavigate()
    const { enqueueSnackbar } = useSnackbar()
    const isAuthenticated = useIsAuthenticated()
    const [email, setEmail] = useState<string>('')
    const [password, setPassword] = useState<string>('')
    const [errors, setErrors] = useState<typeof DEFAULT_ERRORS>(DEFAULT_ERRORS)
    const [showPassword, setShowPassword] = useState<boolean>(false)

    const validateInfo = useCallback(() => {
        const newErrors = {
            email: !email
                ? 'Completá este campo.'
                : !validateEmail(email)
                  ? 'El email ingresado no es válido.'
                  : '',
            password: !password ? 'Completá este campo.' : '',
        }

        setErrors(newErrors)
        return Object.values(newErrors).every((x) => x === '')
    }, [email, password])

    const handleLogin = useCallback(async () => {
        if (!validateInfo()) return
        const response = await new AuthService().login(email, password)
        if (response.status === 200) {
            const userData = response.data as LoginResponse
            if (!userData.validated) {
                enqueueSnackbar('Email no validado. Revisá tu correo.', {
                    variant: 'warning',
                    autoHideDuration: 6000,
                })
            } else if (!userData.approved) {
                enqueueSnackbar(
                    'Usuario pendiente de aprobación. Cuando un administrador lo apruebe te llegará un email.',
                    {
                        variant: 'warning',
                        autoHideDuration: 6000,
                    },
                )
            } else {
                signIn({
                    auth: { token: userData.token, type: 'Bearer' },
                    userState: {
                        picture: userData.picture,
                        fullName: userData.fullName,
                    } as AuthenticatedUserData,
                })
                if (isRootAdmin()) {
                    navigate(AppRoutes.admins)
                } else if (isAdmin()) {
                    navigate(AppRoutes.news)
                } else {
                    navigate(AppRoutes.home)
                }
            }
        } else if (response.status === 401 || response.status === 403) {
            enqueueSnackbar('Usuario o contraseña incorrectos', {
                variant: 'warning',
                autoHideDuration: 6000,
            })
        } else {
            enqueueSnackbar('Error al enviar la solicitud', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        }
    }, [email, enqueueSnackbar, navigate, password, signIn, validateInfo])

    const enterPress = useCallback(
        (event: KeyboardEvent) => {
            if (event.key === 'Enter') handleLogin()
        },
        [handleLogin],
    )

    useEffect(() => {
        if (isAuthenticated) navigate(AppRoutes.home)
        window.addEventListener('keydown', enterPress)

        var error = localStorage.getItem('error')

        if (error === '1') {
            enqueueSnackbar('El link de activación expiró. Se envió uno nuevo a tu correo.', {
                variant: 'warning',
                autoHideDuration: 6000,
            })
        }

        return () => window.removeEventListener('keydown', enterPress)
    }, [isAuthenticated, enterPress, navigate, enqueueSnackbar])

    return (
        <CustomPaper className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] md:w-[450px] py-6">
            <div className="flex items-center justify-center gap-6 px-2 mb-8 w-[85%]">
                <img
                    src="/logo.png"
                    alt="logo"
                    className="h-[45px] md:h-[60px]"
                />
                <span className="text-primary font-semibold text-base md:text-xl">
                    Sistema de seguimiento de graduados
                </span>
            </div>
            <span className="text-secondary text-xl">Iniciar Sesión</span>
            <Divider />
            <div className="flex flex-col gap-6 mt-8">
                <CustomInput
                    type="text"
                    label="Email"
                    placeholder="Ingresá tu email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    errorText={errors.email}
                />
                <CustomInput
                    type={showPassword ? 'text' : 'password'}
                    label="Contraseña"
                    placeholder="Ingresá tu contraseña"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    errorText={errors.password}
                    iconButtons={[
                        {
                            icon: showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />,
                            action: () => setShowPassword(!showPassword),
                        },
                    ]}
                />
            </div>
            <CustomButton
                text="Ingresar"
                className="w-full mt-6 h-[36px]"
                onClick={handleLogin}
            />
            <div className="flex flex-col gap-2 mt-4">
                <div className="flex gap-2">
                    <span className="text-sm md:text-base">¿Olvidaste la contraseña?</span>
                    <span
                        className="text-sm md:text-base text-primary cursor-pointer underline"
                        onClick={() => navigate('/recover-password')}
                    >
                        Recuperala acá
                    </span>
                </div>
                <div className="flex items-center justify-center gap-6 px-2 mt-4">
                    <Divider className="flex w-[44%]" />
                    <span className="text-primary">o</span>
                    <Divider className="flex w-[44%]" />
                </div>
                <CustomButton
                    text="Ingresar con LinkedIn"
                    onClick={() => {
                        window.location.href = linkedinOAuthURL
                    }}
                    className="w-full mt-2 bg-linkedin hover:bg-hoverLinkedin"
                    startIcon={<LinkedInIcon sx={{ width: '1.5rem', height: '1.5rem' }} />}
                />
                <div className="flex items-center justify-center gap-2 mt-2">
                    <span className="text-sm md:text-base">¿Sos nuevo?</span>
                    <span
                        className="text-sm md:text-base text-center text-primary cursor-pointer underline"
                        onClick={() => navigate('/register')}
                    >
                        Registrate acá
                    </span>
                </div>
            </div>
        </CustomPaper>
    )
}

export default LoginPage
